// Full-screen-overlay policy editor modal (Phase 5A UI polish).
//
// Opens over a darkening overlay (Radix Dialog). Header shows the policy name +
// version/status; below it EN/TR tabs; a WYSIWYG editor (Tiptap) fills the body
// and scrolls; a controlling-language disclaimer sits under it; a sticky footer
// carries Cancel / Save draft, plus a Publish action once both languages are
// complete. A published version opens read-only with a "Create new version"
// action. Technical key/org_type inputs never appear here — audience is a
// separate "Manage audience" modal; publishing goes through a confirm modal.

import { useMemo, useState } from 'react'
import { Users, AlertTriangleIcon } from 'lucide-react'
import { DialogContent } from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { Button } from '../../../components/ui/button'
import { RichTextEditor } from '../../../components/ui/RichTextEditor'
import { AgreementsError } from '../../../api/agreements'
import * as api from '../../../api/agreementsAdmin'
import { useT } from '../../../i18n'
import { policiesI18n } from '../../../i18n/admin/policies'
import {
  AGREEMENT_EN,
  SUPPORTED_AGREEMENT_LOCALES,
  agreementLocaleLongLabel,
} from '../../../i18n/agreement-locales'
import type { PolicyRowState } from './PoliciesPage'
import { AudienceModal } from './AudienceModal'
import { PublishConfirmModal } from './PublishConfirmModal'

function slugify(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'policy'
}

type LocaleContent = Record<string, string>

export function PolicyEditorModal({
  row,
  isNew = false,
  onClose,
  onChanged,
}: {
  row: PolicyRowState
  isNew?: boolean
  onClose: () => void
  onChanged: () => void | Promise<void>
}) {
  const t = useT(policiesI18n)

  const initialContent = useMemo<LocaleContent>(() => {
    const c: LocaleContent = {}
    for (const loc of SUPPORTED_AGREEMENT_LOCALES) {
      c[loc] = row.activeVersion?.localizations.find((l) => l.locale === loc)?.content ?? ''
    }
    return c
  }, [row])

  const [agreementId, setAgreementId] = useState<string | null>(row.agreement?.id ?? null)
  const [versionId, setVersionId] = useState<string | null>(
    row.status === 'draft' ? (row.draft?.id ?? null) : null,
  )
  const [readOnly, setReadOnly] = useState(row.status === 'published')
  const [name, setName] = useState(row.name)
  const [reaccept, setReaccept] = useState(row.activeVersion?.reaccept_required ?? false)
  const [content, setContent] = useState<LocaleContent>(initialContent)
  const [activeLocale, setActiveLocale] = useState(AGREEMENT_EN)
  const [persistedLocales, setPersistedLocales] = useState<string[]>(row.presentLocales)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audienceOpen, setAudienceOpen] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)

  const editable = !readOnly
  const versionNumber =
    row.activeVersion?.version ?? (row.published ? row.published.version + 1 : 1)
  const statusLabel = readOnly
    ? t.statusPublished
    : row.status === 'none'
      ? t.statusNew
      : t.statusDraft

  const bothComplete = SUPPORTED_AGREEMENT_LOCALES.every((l) => persistedLocales.includes(l))
  const missing = SUPPORTED_AGREEMENT_LOCALES.filter((l) => !persistedLocales.includes(l))

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true)
    setError(null)
    try {
      return await fn()
    } catch (e) {
      setError(e instanceof AgreementsError ? e.message : String(e))
      return undefined
    } finally {
      setBusy(false)
    }
  }

  async function ensureDraft(): Promise<{ aId: string; vId: string } | null> {
    let aId = agreementId
    if (!aId) {
      const key = row.key || slugify(name)
      const created = await api.createPolicy({
        key,
        name: name.trim() || key,
        per_user_ack: row.perUserAck,
        is_marketing_consent: row.isMarketing,
      })
      aId = created.id
      setAgreementId(aId)
    }
    let vId = versionId
    if (!vId) {
      const v = await api.createVersion(aId, { reaccept_required: reaccept })
      vId = v.id
      setVersionId(vId)
    }
    return { aId, vId }
  }

  async function saveDraft() {
    if (!name.trim()) {
      setError(t.nameRequired)
      return
    }
    await run(async () => {
      const ids = await ensureDraft()
      if (!ids) return
      let last: api.PolicyVersion | null = null
      for (const loc of SUPPORTED_AGREEMENT_LOCALES) {
        if (content[loc]?.trim()) {
          last = await api.upsertLocalization(ids.aId, ids.vId, loc, content[loc])
        }
      }
      if (last) setPersistedLocales(last.present_locales)
      await onChanged()
    })
  }

  async function createNewVersion() {
    await run(async () => {
      if (!agreementId) return
      const v = await api.createVersion(agreementId, { reaccept_required: reaccept })
      setVersionId(v.id)
      setReadOnly(false)
      setPersistedLocales([])
      await onChanged()
    })
  }

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[92vh] w-full max-w-2xl sm:max-w-2xl flex-col gap-0 overflow-hidden p-0">
        {/* Header */}
        <div className="border-b border-slate-100 px-5 py-4">
          {isNew && !agreementId ? (
            <input
              aria-label={t.nameLabel}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.namePlaceholder}
              className="w-full rounded border border-slate-300 px-2 py-1 text-base font-semibold text-slate-900"
              data-testid="policy-name-input"
            />
          ) : (
            <h2 className="text-base font-semibold text-slate-900">{name}</h2>
          )}
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
            <span>
              {t.version} {versionNumber}
            </span>
            <span className="text-slate-300">·</span>
            <span>{statusLabel}</span>
          </div>
        </div>

        {/* Locale tabs */}
        <div className="flex gap-1 border-b border-slate-100 px-5 pt-2">
          {SUPPORTED_AGREEMENT_LOCALES.map((loc) => {
            const has = persistedLocales.includes(loc) || content[loc]?.trim()
            return (
              <button
                key={loc}
                type="button"
                onClick={() => setActiveLocale(loc)}
                className={`flex items-center gap-1.5 rounded-t px-3 py-2 text-sm font-medium ${
                  activeLocale === loc
                    ? 'border-b-2 border-emerald-600 text-emerald-700'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`editor-tab-${loc}`}
              >
                {agreementLocaleLongLabel(loc)}
                {loc === AGREEMENT_EN && (
                  <span className="text-[10px] font-normal text-slate-400">{t.controlling}</span>
                )}
                <span className={has ? 'text-emerald-500' : 'text-slate-300'}>●</span>
              </button>
            )
          })}
        </div>

        {/* Body: editor */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div
              role="alert"
              className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </div>
          )}
          <RichTextEditor
            key={activeLocale}
            value={content[activeLocale] ?? ''}
            editable={editable}
            ariaLabel={`${t.contentHtml} ${activeLocale}`}
            onChange={(html) => setContent((c) => ({ ...c, [activeLocale]: html }))}
          />
          <p className="mt-3 text-xs text-slate-400">{t.controllingDisclaimer}</p>
        </div>

        {/* Sticky footer */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAudienceOpen(true)}
              disabled={busy || !agreementId}
              title={!agreementId ? t.saveFirstForAudience : undefined}
              data-testid="manage-audience-btn"
            >
              <Users className="mr-1.5 h-4 w-4" />
              {t.manageAudience}
            </Button>
            {editable && (
              <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={reaccept}
                  onChange={(e) => setReaccept(e.target.checked)}
                />
                {t.reacceptRequired}
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
              {t.cancel}
            </Button>
            {readOnly ? (
              <Button size="sm" onClick={createNewVersion} disabled={busy} data-testid="new-version-btn">
                {t.createNewVersion}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={saveDraft}
                  disabled={busy}
                  data-testid="save-draft-btn"
                >
                  {t.saveDraft}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setPublishOpen(true)}
                  disabled={busy || !bothComplete}
                  title={!bothComplete ? `${t.missing}: ${missing.join(', ')}` : undefined}
                  data-testid="publish-btn"
                >
                  {t.publish}
                </Button>
              </>
            )}
          </div>
        </div>

        {!bothComplete && editable && (
          <div className="flex items-center gap-1.5 border-t border-amber-100 bg-amber-50 px-5 py-1.5 text-[11px] text-amber-700">
            <AlertTriangleIcon className="h-3.5 w-3.5" />
            {t.publishGateHint}
          </div>
        )}
      </DialogContent>
      </Dialog>

      {audienceOpen && agreementId && (
        <AudienceModal
          agreementId={agreementId}
          existing={row.agreement?.applicability ?? []}
          onClose={() => setAudienceOpen(false)}
          onSaved={onChanged}
        />
      )}
      {publishOpen && agreementId && versionId && (
        <PublishConfirmModal
          agreementId={agreementId}
          versionId={versionId}
          versionNumber={versionNumber}
          reacceptRequired={reaccept}
          audience={row.agreement?.applicability ?? []}
          onClose={() => setPublishOpen(false)}
          onPublished={async () => {
            setPublishOpen(false)
            await onChanged()
            onClose()
          }}
        />
      )}
    </>
  )
}
